import React, { useState } from 'react';
import { Mail, MapPin, Clock, Phone, Send, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { siteConfig, contactData } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        service: formData.service || null,
        message: formData.message
      });
      
      toast({
        title: "Message Sent!",
        description: response.data.message || "Thank you for reaching out. Lacey will get back to you soon.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        service: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Toaster />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#f9f9f6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
            {contactData.hero.subtitle}
          </span>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6">
            {contactData.hero.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {contactData.hero.description}
          </p>
        </div>
      </section>

      {/* Scroll Down Prompt */}
      <section className="py-6 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <button 
            onClick={() => {
              document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-[#8B9D77] font-medium flex items-center justify-center gap-2 mx-auto hover:text-[#6B7A5D] transition-colors cursor-pointer"
          >
            <span>Click here to scroll down and send a message</span>
            <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-serif text-gray-800 mb-4">
            {contactData.intro.title}
          </h2>
          <p className="text-gray-600">
            {contactData.intro.description}
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#f5f5f0] border-0 p-6 rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-[#d4dcc4] flex items-center justify-center mx-auto mb-4">
                <Mail className="w-5 h-5 text-[#6B7A5D]" />
              </div>
              <p className="text-sm text-gray-500 mb-1">Email</p>
              <a href={`mailto:${siteConfig.email}`} className="text-gray-800 font-medium text-sm hover:text-[#8B9D77] transition-colors">
                {siteConfig.email}
              </a>
            </Card>

            <Card className="bg-[#f5f5f0] border-0 p-6 rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-[#d4dcc4] flex items-center justify-center mx-auto mb-4">
                <Phone className="w-5 h-5 text-[#6B7A5D]" />
              </div>
              <p className="text-sm text-gray-500 mb-1">Phone</p>
              <p className="text-gray-800 font-medium text-sm">{siteConfig.phone}</p>
            </Card>

            <Card className="bg-[#f5f5f0] border-0 p-6 rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-[#d4dcc4] flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-5 h-5 text-[#6B7A5D]" />
              </div>
              <p className="text-sm text-gray-500 mb-1">Service Area</p>
              <p className="text-gray-800 font-medium text-sm">{siteConfig.location}</p>
            </Card>

            <Card className="bg-[#f5f5f0] border-0 p-6 rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-[#d4dcc4] flex items-center justify-center mx-auto mb-4">
                <Clock className="w-5 h-5 text-[#6B7A5D]" />
              </div>
              <p className="text-sm text-gray-500 mb-1">Response Time</p>
              <p className="text-gray-800 font-medium text-sm">{siteConfig.responseTime}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Note from Lacey & Form Section */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Note from Lacey */}
            <div>
              <Card className="bg-gradient-to-br from-[#d4dcc4] to-[#c9b896] border-0 p-8 rounded-2xl h-full">
                <h3 className="text-xl font-serif text-gray-800 mb-4">
                  {contactData.laceyNote.title}
                </h3>
                <p className="text-gray-700 leading-relaxed italic">
                  "{contactData.laceyNote.text}"
                </p>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-serif text-gray-800 mb-6">
                Send a Message
              </h2>
              <p className="text-gray-600 text-sm mb-6">
                Fill out the form below and Lacey will be in touch soon.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="rounded-lg border-gray-200 focus:border-[#8B9D77] focus:ring-[#8B9D77]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="rounded-lg border-gray-200 focus:border-[#8B9D77] focus:ring-[#8B9D77]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="rounded-lg border-gray-200 focus:border-[#8B9D77] focus:ring-[#8B9D77]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">Service Interest</Label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#8B9D77] focus:ring-[#8B9D77] focus:outline-none"
                  >
                    <option value="">Select a service</option>
                    {contactData.serviceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Your Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="rounded-lg border-gray-200 focus:border-[#8B9D77] focus:ring-[#8B9D77]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#8B9D77] hover:bg-[#7A8B68] text-white px-8 py-5 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg flex items-center gap-2"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </Button>

                <p className="text-gray-500 text-xs">
                  {contactData.formNote}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-[#f9f9f6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl italic text-gray-700 font-serif mb-2">
            "{contactData.quote.text}"
          </p>
          <p className="text-[#A88B4A] font-medium">{contactData.quote.reference}</p>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
